/**
 * AgencyFeedConfig — Piano Agenzia Pro (€69) — Configurazione Feed XML/CSV.
 *
 * L'utente incolla l'URL del feed del suo gestionale, il sistema valida e sincronizza.
 * Ogni 12h il backend rilancia la sync in background.
 * Errori mostrati esclusivamente in dashboard (no email).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    Radar, CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCw,
    Trash2, ArrowRight, ExternalLink, Info, Sparkles,
} from "lucide-react";

const STATUS_UI = {
    ok:      { icon: CheckCircle2, tone: "text-emerald-400 border-emerald-600/40 bg-emerald-900/20", label: "Sincronizzazione riuscita" },
    partial: { icon: AlertTriangle, tone: "text-amber-300 border-amber-600/40 bg-amber-900/20", label: "Sincronizzato con avvisi" },
    error:   { icon: XCircle, tone: "text-red-400 border-red-600/40 bg-red-900/20", label: "Errore nella sincronizzazione" },
    pending: { icon: Loader2, tone: "text-blue-300 border-blue-600/40 bg-blue-900/20", label: "Sincronizzazione in corso" },
};

export default function AgencyFeedConfig() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [feed, setFeed] = useState(null);
    const [loading, setLoading] = useState(true);
    const [url, setUrl] = useState("");
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!user) return;
        api.get("/agency/feed")
            .then((r) => {
                const f = r.data && r.data.id ? r.data : null;
                setFeed(f);
                if (f) setUrl(f.url);
            })
            .catch((e) => {
                if (e?.response?.status === 403) setError({ blocked: true, msg: e.response.data.detail });
                else if (e?.response?.status === 402) setError({ blocked: true, msg: e.response.data.detail });
                else setError({ msg: "Errore nel caricamento configurazione feed" });
            })
            .finally(() => setLoading(false));
    }, [user]);

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const r = await api.post("/agency/feed", { url });
            setFeed(r.data);
        } catch (e) {
            setError({ msg: e?.response?.data?.detail || "Errore validazione feed" });
        } finally {
            setSaving(false);
        }
    };

    const syncNow = async () => {
        setSyncing(true);
        setError(null);
        try {
            const r = await api.post("/agency/feed/sync");
            setFeed(r.data);
        } catch (e) {
            setError({ msg: e?.response?.data?.detail || "Errore sincronizzazione" });
        } finally {
            setSyncing(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("Rimuovere la configurazione feed? Gli annunci sincronizzati verranno messi in pausa.")) return;
        setDeleting(true);
        try {
            await api.delete("/agency/feed");
            setFeed(null); setUrl("");
        } catch (e) { console.error(e); }
        finally { setDeleting(false); }
    };

    if (!user) return <div className="pt-32 text-center text-ink-400">Effettua l&apos;accesso.</div>;
    if (loading) return <div className="pt-32 text-center text-ink-400">Caricamento configurazione feed…</div>;

    // Blocked (wrong plan)
    if (error?.blocked) {
        return (
            <div className="pt-32 pb-24 max-w-2xl mx-auto px-6 text-center">
                <Radar size={36} className="text-purple-300 mx-auto mb-4" />
                <h1 className="font-serif text-3xl text-ink-50">Configurazione Feed</h1>
                <p className="text-ink-400 mt-3 mb-8 leading-relaxed">{error.msg}</p>
                <Link to="/prezzi" className="cta-gold px-6 py-3 text-sm">Vedi i piani agenzia →</Link>
            </div>
        );
    }

    const StatusIcon = feed && STATUS_UI[feed.last_status]?.icon;
    const statusTone = feed && STATUS_UI[feed.last_status]?.tone;
    const statusLabel = feed && (STATUS_UI[feed.last_status]?.label || feed.last_status);

    return (
        <div data-testid="agency-feed-page" className="pt-32 pb-24 w-full px-6 md:px-12 lg:px-20 2xl:px-28" style={{ colorScheme: "dark" }}>
            {/* Header */}
            <div className="mb-10 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={16} className="text-[#D4AF6A]" />
                        <span className="label-eyebrow text-[#D4AF6A]">Piano Agenzia Pro · €69/mese</span>
                    </div>
                    <h1 className="font-serif text-4xl text-ink-50">Configurazione Feed</h1>
                    <p className="text-ink-400 mt-3 max-w-xl">
                        Collega il feed XML o CSV del tuo gestionale: gli annunci verranno sincronizzati automaticamente ogni 12 ore.
                    </p>
                </div>
                <Link to="/dashboard" className="cta-outline px-4 py-2 text-sm">← Dashboard</Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left: URL + status */}
                <div className="lg:col-span-2 space-y-6">
                    {/* URL card */}
                    <div className="rounded-xl border border-white/10 p-6" style={{ backgroundColor: "#111111" }}>
                        <label className="block text-[13px] uppercase tracking-[0.14em] font-semibold text-white mb-3">
                            URL del feed
                        </label>
                        <div className="flex gap-2 flex-wrap">
                            <input
                                data-testid="feed-url-input"
                                type="url"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="https://gestionale.example.it/export/annunci.xml"
                                className="flex-1 min-w-[240px] bg-black/40 border border-white/10 px-3 py-2.5 rounded-lg text-[14px] text-white focus:border-[#D4AF6A]/60 outline-none placeholder:text-ink-500"
                            />
                            <button
                                data-testid="feed-save-btn"
                                onClick={save}
                                disabled={saving || !url.trim()}
                                className="cta-gold px-5 py-2.5 text-[13px] font-medium disabled:opacity-40"
                            >
                                {saving ? "Validazione…" : feed ? "Aggiorna" : "Salva e sincronizza"}
                            </button>
                        </div>
                        {error && !error.blocked && (
                            <div data-testid="feed-error" className="mt-4 flex items-start gap-2.5 p-3 rounded-lg bg-red-900/25 border border-red-700/40 text-[13px] text-red-300">
                                <XCircle size={16} className="shrink-0 mt-0.5" />
                                <div>{error.msg}</div>
                            </div>
                        )}
                    </div>

                    {/* Status card */}
                    {feed && (
                        <div className="rounded-xl border border-white/10 p-6" style={{ backgroundColor: "#111111" }}>
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-[13px] uppercase tracking-[0.14em] font-semibold text-white">Stato ultimo sync</span>
                                <div className="flex gap-2">
                                    <button
                                        data-testid="feed-sync-btn"
                                        onClick={syncNow}
                                        disabled={syncing}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] text-ink-200 border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors disabled:opacity-40"
                                    >
                                        {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                                        Sincronizza ora
                                    </button>
                                    <button
                                        data-testid="feed-delete-btn"
                                        onClick={remove}
                                        disabled={deleting}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] text-red-300 border border-red-800/50 hover:border-red-500 hover:bg-red-900/20 transition-colors disabled:opacity-40"
                                    >
                                        <Trash2 size={12} /> Rimuovi
                                    </button>
                                </div>
                            </div>

                            {StatusIcon && (
                                <div data-testid="feed-status" className={`flex items-start gap-2.5 p-3 rounded-lg border ${statusTone}`}>
                                    <StatusIcon size={16} className={`shrink-0 mt-0.5 ${feed.last_status === "pending" ? "animate-spin" : ""}`} />
                                    <div className="text-[13px]">
                                        <div className="font-medium">{statusLabel}</div>
                                        {feed.last_error && (
                                            <div className="mt-1 text-[12px] opacity-90 whitespace-pre-wrap">{feed.last_error}</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
                                <Stat label="Sincronizzati" value={feed.last_synced_count} />
                                <Stat label="Nuovi" value={feed.added} tone="emerald" />
                                <Stat label="Aggiornati" value={feed.updated} tone="blue" />
                                <Stat label="Disattivati" value={feed.deactivated} tone="amber" />
                            </div>

                            <div className="mt-4 text-[12px] text-ink-500 flex items-center gap-2 flex-wrap">
                                <span>Formato:</span> <span className="text-ink-300">{feed.format?.toUpperCase() || "—"}</span>
                                <span>·</span>
                                <span>Ultimo sync:</span>
                                <span className="text-ink-300">{feed.last_sync_at ? new Date(feed.last_sync_at).toLocaleString("it-IT") : "—"}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right: 3-step guide */}
                <aside className="space-y-6">
                    <div className="rounded-xl border border-[#D4AF6A]/25 p-6" style={{ backgroundColor: "#0F0D08" }}>
                        <div className="flex items-center gap-2 mb-4">
                            <Info size={15} className="text-[#D4AF6A]" />
                            <span className="text-[13px] uppercase tracking-[0.14em] font-semibold text-white">Come collegare il feed</span>
                        </div>
                        <ol className="space-y-4">
                            <Step n={1} title="Esporta il feed">
                                Dal tuo gestionale (Gestim, Immobiliare.it, WordPress WPResidence…) genera l&apos;export XML o CSV degli annunci attivi.
                            </Step>
                            <Step n={2} title="Copia l'URL pubblico">
                                Assicurati che il link sia accessibile senza login (o con token pubblico) e copialo negli appunti.
                            </Step>
                            <Step n={3} title="Incolla e salva">
                                Incolla l&apos;URL qui accanto e clicca <strong className="text-[#D4AF6A]">Salva e sincronizza</strong>. La prima verifica avviene subito.
                            </Step>
                        </ol>
                    </div>

                    <div className="rounded-xl border border-white/8 p-5" style={{ backgroundColor: "#111111" }}>
                        <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-white mb-3">Campi obbligatori</div>
                        <ul className="text-[13px] text-ink-300 space-y-1.5">
                            <li>• <b className="text-white">id</b> · codice univoco annuncio</li>
                            <li>• <b className="text-white">titolo</b></li>
                            <li>• <b className="text-white">prezzo</b> · numero positivo</li>
                            <li>• <b className="text-white">città</b></li>
                            <li>• <b className="text-white">tipologia</b> · es. appartamento</li>
                            <li>• <b className="text-white">contratto</b> · vendita / affitto</li>
                        </ul>
                        <div className="mt-4 text-[11px] text-ink-500 leading-relaxed">
                            Campi opzionali riconosciuti: descrizione, indirizzo, locali, bagni, mq, foto.
                            <br />Alias italiani supportati (es. <code>citta</code>, <code>comune</code>, <code>tipologia</code>).
                        </div>
                    </div>

                    <div className="rounded-xl border border-blue-800/40 p-5" style={{ backgroundColor: "#0A1220" }}>
                        <div className="text-[13px] text-blue-100 mb-2 flex items-center gap-2">
                            <ArrowRight size={14} /> Serve il CRM completo?
                        </div>
                        <p className="text-[12px] text-blue-200/80 leading-relaxed mb-3">
                            Passa al piano <strong>Agenzia + VD Manager</strong> per ottenere Pipeline, Pratiche, AI Assistant e Lead proprietari.
                        </p>
                        <Link
                            to="/prezzi"
                            className="inline-flex items-center gap-1.5 text-[12px] text-blue-300 hover:text-blue-100 transition-colors"
                            onClick={() => navigate("/prezzi")}
                        >
                            Confronta i piani <ExternalLink size={11} />
                        </Link>
                    </div>
                </aside>
            </div>
        </div>
    );
}

function Step({ n, title, children }) {
    return (
        <li className="flex items-start gap-3">
            <span className="w-7 h-7 rounded-full bg-[#D4AF6A] text-black text-[13px] font-semibold flex items-center justify-center shrink-0">{n}</span>
            <div>
                <div className="text-[14px] font-medium text-white leading-snug">{title}</div>
                <div className="text-[12px] text-ink-400 mt-1 leading-relaxed">{children}</div>
            </div>
        </li>
    );
}

function Stat({ label, value, tone }) {
    const bg = { emerald: "bg-emerald-900/30", blue: "bg-blue-900/30", amber: "bg-amber-900/30" }[tone] || "bg-white/5";
    return (
        <div className={`rounded-lg p-3 ${bg}`}>
            <div className="text-[11px] uppercase tracking-wide text-ink-400">{label}</div>
            <div className="text-[22px] font-semibold text-white mt-1">{value ?? 0}</div>
        </div>
    );
}
